'use client';

/**
 * NativeAppInit — one-time initialization for Capacitor native apps.
 *
 * Included in app/layout.tsx. On web it renders nothing and does nothing.
 * On native it:
 * 1. Sets status bar style
 * 2. Hides the splash screen after load
 * 3. Configures keyboard resize behavior
 * 4. Initializes deep link listeners
 * 5. Initializes push notification listeners
 * 6. Installs the fetch interceptor (via side-effect import)
 */

import { useEffect } from 'react';
import { isNative } from '@/lib/platform';
import { navigateTo } from '@/lib/native-navigation';
import { clearContentCache } from '@/lib/content-api';

export default function NativeAppInit() {
  useEffect(() => {
    if (!isNative()) return;

    async function init() {
      // Mark the document as native for CSS targeting
      document.documentElement.classList.add('native-app');

      // Import fetch interceptor (side effect — installs the monkey-patch)
      await import('@/lib/fetch-interceptor');

      // Status bar
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#0A1E2E' });
      } catch {
        // StatusBar not available on this platform
      }

      // Hide splash screen
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide({ fadeOutDuration: 300 });
      } catch {
        // SplashScreen not available
      }

      // Deep links
      try {
        const { initDeepLinks, onAppResume } = await import('@/lib/deep-links');
        const { handleAuthCallback } = await import('@/lib/auth/NativeAuthProvider');

        initDeepLinks({
          onNavigate: (path) => {
            navigateTo(path);
          },
          onAuthCallback: async (url) => {
            const success = await handleAuthCallback(url);
            if (success) {
              navigateTo('/dashboard');
            }
          },
        });

        // Re-check biometric lock and refresh content on app resume
        onAppResume(async () => {
          try {
            const { isBiometricLockEnabled, authenticateWithBiometrics } =
              await import('@/lib/biometric-auth');
            const enabled = await isBiometricLockEnabled();
            if (enabled) {
              await authenticateWithBiometrics('Unlock The Art of CTO');
            }
          } catch {
            // Biometric check failed — continue anyway
          }

          // Refresh stale content on resume
          clearContentCache();
        });
      } catch {
        // Deep link init failed
      }

      // Push notification listeners
      try {
        const { setupPushListeners } = await import('@/lib/native-push');
        setupPushListeners({
          onNavigate: (url) => {
            navigateTo(url);
          },
        });
      } catch {
        // Push init failed
      }

      // App shortcuts (long-press quick actions)
      try {
        const { setupAppShortcutListener } = await import('@/lib/app-shortcuts');
        setupAppShortcutListener((url) => {
          navigateTo(url);
        });
      } catch {
        // App shortcuts not supported
      }

      // Register Android notification channels
      try {
        const { registerNativeNotificationChannels } = await import(
          '@/lib/native-notifications'
        );
        await registerNativeNotificationChannels();
      } catch {
        // Notification channels not supported
      }
    }

    init();
  }, []);

  // Renders nothing
  return null;
}
